import type { PublishToEventItem } from '@app/routes/content/domain';
import { selectRecordingEventInfo } from '@app/routes/content/routes/recordings/selector';
import {
  selectPublishToEventsDraftSelectedEvents,
  selectPublishToEventsEventAreaValidationErrors,
  selectPublishToEventsEventsList,
  selectPublishToEventsInitialSelectedEvents,
  selectPublishToEventsPageInfo,
  selectPublishToEventsStatus,
  selectPublishToEventsVideoAreasByEventId,
} from '@app/routes/content/selectors';
import { useAppDispatch, useAppSelector } from '@app/store/store';
import { getBaseUrl } from '@hopin-team/dashboard-constants';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useCallback } from 'react';

import { createEventPartOption, createEventPartOptions } from './constants';
import { EventItem } from './event-item';
import type { EventPart, EventPartOption } from './types';
import { supportsUnifiedRecordingsModel } from './utils';

export function EventListContainer() {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const eventsList = useAppSelector(selectPublishToEventsEventsList);
  const videoAreasByEventId = useAppSelector(
    selectPublishToEventsVideoAreasByEventId,
  );
  const pageInfo = useAppSelector(selectPublishToEventsPageInfo);
  const status = useAppSelector(selectPublishToEventsStatus);
  const eventInfo = useAppSelector(selectRecordingEventInfo);
  const selectedEvents = useAppSelector(
    selectPublishToEventsDraftSelectedEvents,
  );
  const initialSelectedEvents = useAppSelector(
    selectPublishToEventsInitialSelectedEvents,
  );
  const eventAreaValidationErrors = useAppSelector(
    selectPublishToEventsEventAreaValidationErrors,
  );

  const isFetching = status === 'processing';
  const hasError = status === 'failed';
  const shouldRenderList = eventsList.length > 0 || isFetching;

  const handleLoadMore = useCallback(() => {
    if (pageInfo.hasNextPage && status !== 'processing') {
      dispatch({ type: '[content] publish-to-events list requested' });
    }
  }, [dispatch, pageInfo.hasNextPage, status]);

  const handleScroll = useCallback(
    (e: React.UIEvent<HTMLUListElement>) => {
      const target = e.currentTarget;
      const threshold = 100;
      const scrollBottom =
        target.scrollHeight - target.scrollTop - target.clientHeight;

      if (scrollBottom < threshold) {
        handleLoadMore();
      }
    },
    [handleLoadMore],
  );

  const handleAreaChange = useCallback(
    (eventId: string, eventPart: EventPart) => {
      dispatch({
        type: '[content] publish-to-events event area changed',
        payload: {
          eventId,
          eventPart,
        },
      });
    },
    [dispatch],
  );

  const handleRetryVideoAreas = useCallback(
    (eventId: string) => {
      dispatch({
        type: '[content] publish-to-events video areas requested',
        payload: { eventId },
      });
    },
    [dispatch],
  );

  const handleSelect = useCallback(
    (event: PublishToEventItem) => {
      dispatch({
        type: '[content] publish-to-events event selection changed',
        payload: {
          eventId: event.id,
        },
      });
    },
    [dispatch],
  );

  return (
    <section
      aria-label={t('content-recording.select-events-to-publish-to')}
      className="h-96"
    >
      {shouldRenderList && (
        <ul
          tabIndex={-1}
          onScroll={handleScroll}
          className={cx(
            'pl-1 pr-2 pt-4 pb-5 m-0',
            'list-none h-96 overflow-x-hidden overflow-y-auto',
            'space-y-3',
            'scrollbar-thin scrollbar-thumb-gray-650 scrollbar-track-transparent',
            '[scrollbar-gutter:stable] [&:has([aria-expanded=true])]:overflow-y-hidden',
          )}
        >
          {eventsList.map(event => {
            const videoAreasState = videoAreasByEventId[event.id];
            const selectedEventPart = selectedEvents[event.id];
            const isEventSelected = selectedEvents?.[event.id] !== undefined;

            // can't assign recordings to source event
            const isSourceEvent = eventInfo?.id === event.id;
            const isUnifiedRecordingsSupported =
              supportsUnifiedRecordingsModel(event);

            const isEventDisabled =
              isSourceEvent ||
              (!isUnifiedRecordingsSupported && !isEventSelected);

            const shouldShowEventPreviewLink =
              isEventSelected && initialSelectedEvents[event.id] !== undefined;

            const eventPreviewUrl = shouldShowEventPreviewLink
              ? `${getBaseUrl()}/organisers/events/${event.slug}/preview`
              : undefined;
            let eventPartOptions: EventPartOption[] = [];

            // Assignments include the saved selected area, but not the full
            // dropdown list. Keep that selected option visible until the
            // slower /video_areas request returns all choices for this event.
            if (videoAreasState?.status === 'success') {
              eventPartOptions = createEventPartOptions(
                videoAreasState.data,
                t('content-recording.replay-area'),
              );
            } else if (selectedEventPart) {
              eventPartOptions = [
                createEventPartOption(
                  selectedEventPart,
                  t('content-recording.replay-area'),
                ),
              ];
            }

            return (
              <li
                key={event.id}
                className={cx('p-1.5 rounded-10', {
                  'bg-spring-neutral-b5': isEventSelected,
                  'bg-spring-neutral-base': !isEventSelected,
                })}
              >
                <EventItem
                  event={event}
                  eventPartOptions={eventPartOptions}
                  tooltipLabel={
                    !isUnifiedRecordingsSupported && !isSourceEvent
                      ? t('content-recording.legacy-replay-model-event')
                      : undefined
                  }
                  hasEventPartValidationError={
                    Boolean(eventAreaValidationErrors[event.id]) &&
                    selectedEvents[event.id] === null
                  }
                  hasFailedEventParts={videoAreasState?.status === 'failed'}
                  isDisabled={isEventDisabled}
                  isFetchingEventParts={
                    videoAreasState?.status === 'processing'
                  }
                  isSelected={isEventSelected}
                  onEventPartChange={handleAreaChange}
                  onEventPartsOpen={handleRetryVideoAreas}
                  onRetryEventParts={handleRetryVideoAreas}
                  onSelect={handleSelect}
                  eventPreviewUrl={eventPreviewUrl}
                  selectedEventPart={selectedEventPart}
                />
              </li>
            );
          })}

          {isFetching &&
            eventsList.length === 0 &&
            Array.from({ length: 5 }).map((_, i) => (
              <li key={`skeleton-initial-${i}`} className="p-1.5">
                <EventItemSkeleton />
              </li>
            ))}

          {isFetching &&
            eventsList.length > 0 &&
            Array.from({ length: 5 }).map((_, i) => (
              <li key={`skeleton-load-more-${i}`} className="p-1.5">
                <EventItemSkeleton />
              </li>
            ))}
        </ul>
      )}

      {hasError && (
        <div className="px-4 py-4 text-center text-spring-neutral-b3 text-sm">
          {t('common.generic-error')}
        </div>
      )}

      {!isFetching && eventsList.length === 0 && !hasError && (
        <div className="px-4 py-8 text-center text-spring-neutral-b3 text-sm">
          {t('content-recording.no-events-found')}
        </div>
      )}
    </section>
  );
}

const EventItemSkeleton = () => (
  <div className="flex items-center gap-3">
    <div className="h-5 w-5 shrink-0 animate-pulse rounded bg-gray-600" />
    <div className="h-8 w-14 shrink-0 animate-pulse rounded-lg bg-gray-600" />
    <div className="flex flex-1 flex-col gap-1">
      <div className="h-[18px] w-[60%] animate-pulse rounded bg-gray-600" />
      <div className="flex items-center gap-2">
        <div className="h-3 w-24 animate-pulse rounded bg-gray-600" />
        <div className="h-4 w-12 animate-pulse rounded bg-gray-600" />
      </div>
    </div>
  </div>
);
